import { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useQuery, keepPreviousData } from '@tanstack/react-query';
import {
  RefreshCw,
  Filter,
  Send,
  FileText,
  Clock,
  AlertTriangle,
  CheckCircle,
  ChevronLeft,
  ChevronRight,
  Bell,
  Plus,
} from 'lucide-react';
import { questionnairesApi } from '@/api/endpoints/questionnaires.api';
import type {
  QuestionnaireSend,
  QuestionnaireResponse,
  SendStatus,
} from '@/types/questionnaire.types';
import {
  SendStatusLabels,
  SendStatusColors,
  DeliveryChannelLabels,
} from '@/types/questionnaire.types';
import { SendQuestionnaireDialog, QuestionnaireSendDetailDialog } from '@/components/features/questionnaires';

type TabType = 'pending' | 'alerts';

export default function QuestionnairesPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  // Read initial filter from URL
  const statusFromUrl = searchParams.get('status') || '';

  const [activeTab, setActiveTab] = useState<TabType>('pending');
  const [page, setPage] = useState(0);
  const pageSize = 10;

  // Filters
  const [statusFilter, setStatusFilter] = useState<string>(statusFromUrl);
  const [questionnaireFilter, setQuestionnaireFilter] = useState<string>('');

  // Dialogs
  const [showSendDialog, setShowSendDialog] = useState(false);
  const [selectedSend, setSelectedSend] = useState<QuestionnaireSend | null>(null);
  const [showDetailDialog, setShowDetailDialog] = useState(false);

  // Query for questionnaire definitions (cached longer)
  const { data: definitions = [] } = useQuery({
    queryKey: ['questionnaire-definitions'],
    queryFn: () => questionnairesApi.getDefinitions(),
    staleTime: 1000 * 60 * 30, // 30 minutes - rarely changes
  });

  // Query for pending sends
  const {
    data: sendsData,
    isLoading: loadingSends,
    refetch: refetchSends,
  } = useQuery({
    queryKey: ['questionnaire-sends', { page, size: pageSize, status: statusFilter, questionnaireCode: questionnaireFilter }],
    queryFn: () => {
      const params: Record<string, unknown> = { page, size: pageSize };
      if (statusFilter) params.status = statusFilter.split(',');
      if (questionnaireFilter) params.questionnaireCode = questionnaireFilter;
      return questionnairesApi.getPendingSends(params as never);
    },
    staleTime: 1000 * 60 * 2, // 2 minutes
    placeholderData: keepPreviousData,
    enabled: activeTab === 'pending',
  });

  // Query for alerts
  const {
    data: alerts = [],
    isLoading: loadingAlerts,
    refetch: refetchAlerts,
  } = useQuery({
    queryKey: ['questionnaire-alerts'],
    queryFn: () => questionnairesApi.getPendingAlerts(),
    staleTime: 1000 * 60 * 2, // 2 minutes
  });

  const sends = sendsData?.content || [];
  const totalPages = sendsData?.totalPages || 0;
  const totalElements = sendsData?.totalElements || 0;
  const loading = activeTab === 'pending' ? loadingSends : loadingAlerts;

  const refresh = () => {
    if (activeTab === 'pending') {
      refetchSends();
    } else {
      refetchAlerts();
    }
  };

  const getStatusBadge = (status: SendStatus) => {
    const colors = SendStatusColors[status] || { bg: 'bg-gray-100', text: 'text-gray-800' };
    const label = SendStatusLabels[status] || status;
    return (
      <span className={`rounded-full px-2 py-1 text-xs font-medium ${colors.bg} ${colors.text}`}>
        {label}
      </span>
    );
  };

  const getRiskBadge = (riskLevel?: string) => {
    if (!riskLevel) return null;
    const riskColors: Record<string, { bg: string; text: string }> = {
      LOW: { bg: 'bg-green-100', text: 'text-green-800' },
      MODERATE: { bg: 'bg-yellow-100', text: 'text-yellow-800' },
      HIGH: { bg: 'bg-orange-100', text: 'text-orange-800' },
      CRITICAL: { bg: 'bg-red-100', text: 'text-red-800' },
    };
    const riskLabels: Record<string, string> = {
      LOW: 'Baixo',
      MODERATE: 'Moderado',
      HIGH: 'Alto',
      CRITICAL: 'Crítico',
    };
    const colors = riskColors[riskLevel] || { bg: 'bg-gray-100', text: 'text-gray-800' };
    return (
      <span className={`rounded-full px-2 py-1 text-xs font-medium ${colors.bg} ${colors.text}`}>
        {riskLabels[riskLevel] || riskLevel}
      </span>
    );
  };

  const formatDate = (dateString?: string) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const clearFilters = () => {
    setStatusFilter('');
    setQuestionnaireFilter('');
    setPage(0);
  };

  const tabClasses = (tab: TabType) =>
    `flex items-center gap-2 px-4 py-2 border-b-2 transition-colors ${
      activeTab === tab
        ? 'border-primary text-primary font-medium'
        : 'border-transparent text-muted-foreground hover:text-foreground'
    }`;

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold">Questionários</h1>
          <p className="text-muted-foreground">
            Gerenciar envios e respostas de questionários
          </p>
        </div>
        <div className="flex gap-2">
          <button
            onClick={refresh}
            className="flex items-center gap-2 rounded-lg border px-4 py-2 hover:bg-muted"
          >
            <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
            Atualizar
          </button>
          <button
            onClick={() => setShowSendDialog(true)}
            className="flex items-center gap-2 rounded-lg bg-primary px-4 py-2 text-primary-foreground hover:bg-primary/90"
          >
            <Plus className="h-4 w-4" />
            Enviar Questionário
          </button>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex border-b">
        <button className={tabClasses('pending')} onClick={() => setActiveTab('pending')}>
          <Send className="h-4 w-4" />
          Envios
        </button>
        <button className={tabClasses('alerts')} onClick={() => setActiveTab('alerts')}>
          <Bell className="h-4 w-4" />
          Alertas
          {alerts.length > 0 && (
            <span className="ml-1 rounded-full bg-red-500 px-2 py-0.5 text-xs text-white">
              {alerts.length}
            </span>
          )}
        </button>
      </div>

      {/* Filters - only for pending tab */}
      {activeTab === 'pending' && (
        <div className="flex flex-wrap items-center gap-4">
          <Filter className="h-4 w-4 text-muted-foreground" />
          <select
            value={statusFilter}
            onChange={(e) => {
              setStatusFilter(e.target.value);
              setPage(0);
            }}
            className="rounded-lg border bg-background px-4 py-2"
          >
            <option value="">Todos os status</option>
            <option value="PENDING">Pendente</option>
            <option value="SENT">Enviado</option>
            <option value="DELIVERED">Entregue</option>
            <option value="ANSWERED">Respondido</option>
            <option value="EXPIRED">Expirado</option>
            <option value="FAILED">Falha</option>
          </select>
          <select
            value={questionnaireFilter}
            onChange={(e) => {
              setQuestionnaireFilter(e.target.value);
              setPage(0);
            }}
            className="rounded-lg border bg-background px-4 py-2"
          >
            <option value="">Todos os questionários</option>
            {definitions.map((def) => (
              <option key={def.code} value={def.code}>
                {def.name}
              </option>
            ))}
          </select>
          {(statusFilter || questionnaireFilter) && (
            <button onClick={clearFilters} className="text-sm text-primary hover:underline">
              Limpar filtros
            </button>
          )}
        </div>
      )}

      {/* Content based on tab */}
      <div className="rounded-lg border bg-card">
        {loading && sends.length === 0 && alerts.length === 0 ? (
          <div className="flex h-64 items-center justify-center">
            <RefreshCw className="h-8 w-8 animate-spin text-primary" />
            <span className="ml-2">Carregando...</span>
          </div>
        ) : activeTab === 'pending' ? (
          /* Pending Sends Table */
          sends.length === 0 ? (
            <div className="flex h-64 flex-col items-center justify-center text-muted-foreground">
              <FileText className="mb-2 h-12 w-12" />
              <p>Nenhum envio encontrado</p>
              {(statusFilter || questionnaireFilter) && (
                <button onClick={clearFilters} className="mt-2 text-primary hover:underline">
                  Limpar filtros
                </button>
              )}
            </div>
          ) : (
            <table className="w-full">
              <thead>
                <tr className="border-b">
                  <th className="px-4 py-3 text-left text-sm font-medium text-muted-foreground">Paciente</th>
                  <th className="px-4 py-3 text-left text-sm font-medium text-muted-foreground">Questionário</th>
                  <th className="px-4 py-3 text-left text-sm font-medium text-muted-foreground">Status</th>
                  <th className="px-4 py-3 text-left text-sm font-medium text-muted-foreground">Canal</th>
                  <th className="px-4 py-3 text-left text-sm font-medium text-muted-foreground">Enviado em</th>
                  <th className="px-4 py-3 text-left text-sm font-medium text-muted-foreground">Expira em</th>
                  <th className="px-4 py-3 text-left text-sm font-medium text-muted-foreground">Ações</th>
                </tr>
              </thead>
              <tbody>
                {sends.map((send) => (
                  <tr key={send.id} className="border-b transition-colors hover:bg-muted/50">
                    <td className="px-4 py-3">
                      <button
                        onClick={() => navigate(`/patients/${send.patientId}`)}
                        className="font-medium text-primary hover:underline"
                      >
                        {send.patientName}
                      </button>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2">
                        <FileText className="h-4 w-4 text-muted-foreground" />
                        <span className="text-sm">{send.questionnaireName}</span>
                      </div>
                    </td>
                    <td className="px-4 py-3">{getStatusBadge(send.status)}</td>
                    <td className="px-4 py-3 text-sm">
                      {send.sentVia ? DeliveryChannelLabels[send.sentVia] : '-'}
                    </td>
                    <td className="px-4 py-3 text-sm">
                      <span className="flex items-center gap-1">
                        <Clock className="h-3 w-3" />
                        {formatDate(send.sentAt)}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-sm">
                      {send.expiresAt ? (
                        <span className={new Date(send.expiresAt) < new Date() ? 'text-red-600' : ''}>
                          {formatDate(send.expiresAt)}
                        </span>
                      ) : '-'}
                    </td>
                    <td className="px-4 py-3">
                      <button
                        onClick={() => {
                          setSelectedSend(send);
                          setShowDetailDialog(true);
                        }}
                        className="text-sm text-primary hover:underline"
                      >
                        Ver detalhes
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )
        ) : (
          /* Alerts Table */
          alerts.length === 0 ? (
            <div className="flex h-64 flex-col items-center justify-center text-muted-foreground">
              <CheckCircle className="mb-2 h-12 w-12 text-green-500" />
              <p>Nenhum alerta pendente</p>
              <p className="text-sm">Todos os alertas foram revisados</p>
            </div>
          ) : (
            <table className="w-full">
              <thead>
                <tr className="border-b">
                  <th className="px-4 py-3 text-left text-sm font-medium text-muted-foreground">Paciente</th>
                  <th className="px-4 py-3 text-left text-sm font-medium text-muted-foreground">Questionário</th>
                  <th className="px-4 py-3 text-left text-sm font-medium text-muted-foreground">Score</th>
                  <th className="px-4 py-3 text-left text-sm font-medium text-muted-foreground">Risco</th>
                  <th className="px-4 py-3 text-left text-sm font-medium text-muted-foreground">Alerta</th>
                  <th className="px-4 py-3 text-left text-sm font-medium text-muted-foreground">Respondido em</th>
                  <th className="px-4 py-3 text-left text-sm font-medium text-muted-foreground">Ações</th>
                </tr>
              </thead>
              <tbody>
                {alerts.map((alert: QuestionnaireResponse) => (
                  <tr key={alert.id} className="border-b transition-colors hover:bg-muted/50">
                    <td className="px-4 py-3">
                      <button
                        onClick={() => navigate(`/patients/${alert.patientId}`)}
                        className="font-medium text-primary hover:underline"
                      >
                        {alert.patientName}
                      </button>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2">
                        <FileText className="h-4 w-4 text-muted-foreground" />
                        <span className="text-sm">{alert.questionnaireName}</span>
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <span className="font-mono font-medium">
                        {alert.totalScore != null && !Number.isNaN(alert.totalScore) ? alert.totalScore : '-'}
                      </span>
                    </td>
                    <td className="px-4 py-3">{getRiskBadge(alert.riskLevel)}</td>
                    <td className="max-w-[250px] px-4 py-3">
                      <div className="flex items-start gap-2">
                        <AlertTriangle className="mt-0.5 h-4 w-4 flex-shrink-0 text-red-500" />
                        <span className="text-sm text-red-700" title={alert.alertMessage}>
                          {alert.alertMessage && alert.alertMessage.length > 80
                            ? alert.alertMessage.substring(0, 80) + '...'
                            : alert.alertMessage || 'Alerta disparado'}
                        </span>
                      </div>
                    </td>
                    <td className="px-4 py-3 text-sm">
                      <span className="flex items-center gap-1">
                        <Clock className="h-3 w-3" />
                        {formatDate(alert.answeredAt)}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <button
                        onClick={() => navigate(`/questionnaires/responses/${alert.id}`)}
                        className="text-sm text-primary hover:underline"
                      >
                        Revisar
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )
        )}

        {/* Pagination - only for pending tab */}
        {activeTab === 'pending' && totalPages > 1 && (
          <div className="flex items-center justify-between border-t px-4 py-3">
            <p className="text-sm text-muted-foreground">
              Página {page + 1} de {totalPages} ({totalElements} envios)
            </p>
            <div className="flex gap-2">
              <button
                onClick={() => setPage((p) => Math.max(0, p - 1))}
                disabled={page === 0}
                className="flex items-center gap-1 rounded border px-3 py-1 text-sm disabled:opacity-50"
              >
                <ChevronLeft className="h-4 w-4" />
                Anterior
              </button>
              <button
                onClick={() => setPage((p) => Math.min(totalPages - 1, p + 1))}
                disabled={page >= totalPages - 1}
                className="flex items-center gap-1 rounded border px-3 py-1 text-sm disabled:opacity-50"
              >
                Próxima
                <ChevronRight className="h-4 w-4" />
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Quick Stats */}
      <div className="grid grid-cols-1 gap-4 md:grid-cols-4">
        <div className="rounded-lg border bg-card p-4">
          <div className="flex items-center gap-2 text-muted-foreground">
            <Send className="h-4 w-4" />
            <span className="text-sm">Pendentes</span>
          </div>
          <p className="mt-2 text-2xl font-bold">
            {sends.filter((s) => s.status === 'PENDING' || s.status === 'SENT').length}
          </p>
        </div>
        <div className="rounded-lg border bg-card p-4">
          <div className="flex items-center gap-2 text-muted-foreground">
            <CheckCircle className="h-4 w-4" />
            <span className="text-sm">Respondidos</span>
          </div>
          <p className="mt-2 text-2xl font-bold">
            {sends.filter((s) => s.status === 'ANSWERED').length}
          </p>
        </div>
        <div className="rounded-lg border bg-card p-4">
          <div className="flex items-center gap-2 text-muted-foreground">
            <Clock className="h-4 w-4" />
            <span className="text-sm">Expirados</span>
          </div>
          <p className="mt-2 text-2xl font-bold">
            {sends.filter((s) => s.status === 'EXPIRED').length}
          </p>
        </div>
        <div className="rounded-lg border bg-card p-4">
          <div className="flex items-center gap-2 text-red-500">
            <AlertTriangle className="h-4 w-4" />
            <span className="text-sm">Alertas</span>
          </div>
          <p className="mt-2 text-2xl font-bold text-red-600">{alerts.length}</p>
        </div>
      </div>

      {/* Send Questionnaire Dialog */}
      <SendQuestionnaireDialog
        open={showSendDialog}
        onOpenChange={setShowSendDialog}
        onSuccess={() => {
          setShowSendDialog(false);
          refresh();
        }}
      />

      {/* Questionnaire Send Detail Dialog */}
      <QuestionnaireSendDetailDialog
        open={showDetailDialog}
        onOpenChange={setShowDetailDialog}
        send={selectedSend}
        onViewResponse={() => {
          if (selectedSend?.responseId) {
            setShowDetailDialog(false);
            navigate(`/questionnaires/responses/${selectedSend.responseId}`);
          }
        }}
      />
    </div>
  );
}
